import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ConversationApi } from '@/Domain/api/Conversation/Conversation.api';

export const ABOVE_ANCHOR_LIMIT = 25;

/** The conversations immediately newer than a deeplinked one, so it can be shown in context. */
export const useConversationsAboveAnchor = (
  baseFilters: Record<string, string>,
  anchorLastMessageAt: string | null,
  anchorId: string | null,
) => {
  const params = useMemo(
    () => ({
      ...baseFilters,
      last_message_at_start_date: anchorLastMessageAt ?? '',
      order: 'asc' as const,
      // The anchor itself plus one sentinel row that only decides whether more exist above.
      limit: ABOVE_ANCHOR_LIMIT + 2,
      offset: 0,
    }),
    [baseFilters, anchorLastMessageAt],
  );

  const isAnchored = Boolean(anchorLastMessageAt);
  const { data, isFetched, isError, refetch } = useQuery({
    queryKey: ['conversations', params],
    queryFn: async () => await ConversationApi.list(params),
    enabled: isAnchored,
    staleTime: 0,
  });

  // Read through a ref so the identity survives anchoring: the socket handler captures this once,
  // and `refetch` ignores `enabled`, so an unguarded call would send the empty
  // `last_message_at_start_date` the unanchored params carry and get a 422 back.
  const isAnchoredRef = useRef(isAnchored);
  useEffect(() => {
    isAnchoredRef.current = isAnchored;
  }, [isAnchored]);

  const refetchAbove = useCallback(async () => {
    if (isAnchoredRef.current) await refetch();
  }, [refetch]);

  return useMemo(() => {
    const items = data?.items ?? [];
    const newer = items.filter((conversation) => conversation.id !== anchorId);
    return {
      // Ends with the anchor: the API truncates timestamps to milliseconds while MySQL stores
      // microseconds, so the older window's `<=` bound can round past the anchor and drop it.
      aboveConversations: [
        ...newer.slice(0, ABOVE_ANCHOR_LIMIT).reverse(),
        ...items.filter((conversation) => conversation.id === anchorId),
      ],
      // `isFetched` is also true after an error, so a failed window renders as an empty one:
      // report it as incomplete too, or the operator loses the way back to the live list.
      hasMoreAbove: newer.length > ABOVE_ANCHOR_LIMIT || isError,
      isAboveFetched: anchorLastMessageAt == null || isFetched,
      refetchAbove,
    };
  }, [data, anchorId, anchorLastMessageAt, isFetched, isError, refetchAbove]);
};
